// @flow
import compact from 'lodash/fp/compact';
import uniq from 'lodash/fp/uniq';

import type { Format } from './date';

import {
  type TabularFile,
  extents,
  cellValue,
  cellByAddress as cellByAddressForFile,
  type TabularCell,
  type CellAddress,
} from 'modules/tabular-file';

export type StockItemAllocation = $ReadOnly<{|
  stockItemKey: string,
  quantity: number,
  warehouseId?: string,
|}>;

export type StockItemAllocationList = $ReadOnlyArray<StockItemAllocation>;

export type TabularOrderShipment = $ReadOnly<{|
  name?: string | null,
  arriveOnDate?: Date | null,
  cancelOnDate?: Date | null,
  locationNumber?: string | null,
  poNumber?: string | null,
  allocations: StockItemAllocationList,
|}>;

export type TabularOrder = $ReadOnly<{|
  customerNumber?: string | null,
  catalogKey?: string | null,
  shipments: $ReadOnlyArray<TabularOrderShipment>,
  customerNumber?: string | null,
|}>;

export type ColumnSpecification = number;
export type RowSpecification = number;

export type CellSpecification = $ReadOnly<{|
  row: RowSpecification,
  column: ColumnSpecification,
|}>;

export type DateCellSpecification = $ReadOnly<{|
  cell: CellSpecification,
  format: Format,
|}>;

export type ColumnCellRangeSpecification = $ReadOnly<{|
  column: ColumnSpecification,
  firstRow?: RowSpecification | null,
  lastRow?: RowSpecification | null,
|}>;

export type StockItemAllocationListSpecification = $ReadOnly<{|
  stockItemKeyColumn: ColumnSpecification,
  quantityColumn: ColumnSpecification,
  firstRow?: RowSpecification | null,
  lastRow?: RowSpecification | null,
  dropZeroes?: boolean,
|}>;

export type TabularOrderShipmentSpecification = $ReadOnly<{|
  name?: CellSpecification | null,
  arriveOnDate?: DateCellSpecification | null,
  cancelOnDate?: DateCellSpecification | null,
  locationNumber?: CellSpecification | null,
  poNumber?: CellSpecification | null,
  allocations: StockItemAllocationListSpecification,
|}>;

export type TabularOrderSpecificationFields = $ReadOnly<{|
  shipments: $ReadOnlyArray<TabularOrderShipmentSpecification>,
  name?: CellSpecification | null,
  customerNumber?: CellSpecification,
  catalogKey?: CellSpecification,
|}>;

export type VersionTest = $ReadOnly<{|
  cell: CellSpecification,
  value: string | RegExp,
|}>;

export type Version = $ReadOnly<{|
  test: VersionTest,
  ...TabularOrderSpecificationFields,
|}>;

export type TabularOrderSpecification = $ReadOnly<{|
  ...TabularOrderSpecificationFields,
  versions?: $ReadOnlyArray<Version>,
  dropEmptyShipments?: boolean,
|}>;

export const KEY_COLUMN_GUESSES: $ReadOnlyArray<string | RegExp> = [
  /upc/i,
  /^ean$/i,
  /sku/i,
  /part/i,
  /item/i,
];

export const QUANTITY_COLUMN_GUESSES: $ReadOnlyArray<string | RegExp> = [
  /quantity/i,
];

export const stringHasContents = (s: string): boolean => !!s.match(/\w+/);

export const stockItemKeyIsValid = (key: string): boolean =>
  stringHasContents(key);

export const validString = (s: string): null | string =>
  stringHasContents(s) ? s.trim() : null;

export const allocationFromFile =
  (
    file: TabularFile,
  ): ((
    specification: StockItemAllocationListSpecification,
  ) => (row: ColumnSpecification) => StockItemAllocation | null) =>
  (specification: StockItemAllocationListSpecification) =>
  (row: ColumnSpecification): StockItemAllocation | null => {
    const cellByAddress = cellByAddressForFile(file);
    const stockItemKeyCell = cellByAddress({
      row,
      column: specification.stockItemKeyColumn,
    });

    if (!stockItemKeyCell) return null;

    const stockItemKey = String(cellValue(stockItemKeyCell)).trim();

    if (!stockItemKeyIsValid(stockItemKey)) return null;

    const quantityCell = cellByAddress({
      row,
      column: specification.quantityColumn,
    });

    const quantityValue = quantityCell ? Number(cellValue(quantityCell)) : 0;

    const quantity = Number.isNaN(quantityValue) ? 0 : quantityValue;

    return { stockItemKey, quantity };
  };

export const stockItemAllocationListFromFile =
  (
    file: TabularFile,
  ): ((
    specification: StockItemAllocationListSpecification,
  ) => StockItemAllocationList) =>
  (
    specification: StockItemAllocationListSpecification,
  ): StockItemAllocationList => {
    const { rows } = extents(file);
    const offset = specification.firstRow || 0;
    const limit =
      (typeof specification.lastRow === 'number'
        ? Math.min(Math.max(specification.lastRow - 1, 0), rows)
        : rows) - offset;

    return compact(
      Array(limit)
        .fill(null)
        .map((_, idx) => idx + offset)
        .map((row) => allocationFromFile(file)(specification)(row))
        .filter((allocation) =>
          specification.dropZeroes ? allocation?.quantity !== 0 : true,
        ),
    );
  };

type StringValueByAddress = (
  cellByAddress: (address: CellAddress) => TabularCell | null,
) => (address: ?CellSpecification) => string | void;

const stringValueByAddress: StringValueByAddress =
  (cellByAddress) => (address) => {
    if (!address) return;

    const cell = cellByAddress(address);

    if (!cell) return;

    // eslint-disable-next-line consistent-return
    return validString(String(cellValue(cell))) || undefined;
  };

type DateValueByAddress = (
  cellByAddress: (address: CellAddress) => TabularCell | null,
) => (address: ?CellSpecification) => Date | void;

const dateValueByAddress: DateValueByAddress = (cellByAddress) => (address) => {
  if (!address) return;

  const cell = cellByAddress(address);

  if (!cell) return;

  const value = cellValue(cell);

  // eslint-disable-next-line consistent-return
  if (value instanceof Date) return value;
  // implicity return undefined
};

type ShipmentFromFile = (
  file: TabularFile,
) => (specification: TabularOrderShipmentSpecification) => TabularOrderShipment;

export const shipmentFromFile: ShipmentFromFile = (file) => (specification) => {
  const cellByAddress = cellByAddressForFile(file);

  const stringValue = stringValueByAddress(cellByAddress);
  const dateValue = dateValueByAddress(cellByAddress);

  const name = stringValue(specification.name);
  const locationNumber = stringValue(specification.locationNumber);
  const poNumber = stringValue(specification.poNumber);

  const arriveOnDate = dateValue(specification?.arriveOnDate?.cell);
  const cancelOnDate = dateValue(specification?.cancelOnDate?.cell);

  return {
    name,
    locationNumber,
    arriveOnDate,
    cancelOnDate,
    poNumber,
    allocations: stockItemAllocationListFromFile(file)(
      specification.allocations,
    ),
  };
};

export const filePassesVersionTest =
  (test: VersionTest): ((file: TabularFile) => boolean) =>
  (file: TabularFile): boolean => {
    const versionCell = cellByAddressForFile(file)(test.cell);

    if (!versionCell) return false;

    const value = cellValue(versionCell);

    if (typeof value === 'number') {
      return value === Number(test.value);
    }

    if (test.value instanceof RegExp) {
      if (typeof value === 'string') {
        return test.value.test(value);
      }
    }

    return String(value).trim() === test.value;
  };

export const shipmentHasQuantities = (
  shipment: TabularOrderShipment,
): boolean => shipment.allocations.some((a) => a.quantity > 0);

export const withoutZeroedShipments = (order: TabularOrder): TabularOrder => ({
  ...order,
  shipments: order.shipments.filter(shipmentHasQuantities),
});

type StringValue = (
  file: TabularFile,
) => (specification?: CellSpecification) => string | null;

export const stringValue: StringValue = (file) => (address) => {
  if (!address) return null;

  const cell = cellByAddressForFile(file)(address);

  if (!cell) return null;

  const value = cellValue(cell);

  return typeof value === 'string' ? value.trim() : null;
};

export const fromTabularFile =
  (
    specification: TabularOrderSpecification,
  ): ((file: TabularFile) => TabularOrder) =>
  (file: TabularFile): TabularOrder => {
    const spec =
      specification.versions?.find((version) =>
        filePassesVersionTest(version.test)(file),
      ) || specification;

    const catalogKey = stringValue(file)(spec.catalogKey);

    const customerNumber = stringValue(file)(spec.customerNumber);

    const order = {
      shipments: spec.shipments.map((shipmentSpecification) =>
        shipmentFromFile(file)(shipmentSpecification),
      ),
      catalogKey,
      customerNumber,
    };

    return specification.dropEmptyShipments
      ? withoutZeroedShipments(order)
      : order;
  };

export const allStockItemKeys = (order: TabularOrder): $ReadOnlyArray<string> =>
  uniq(
    order.shipments
      .flatMap((shipment) => shipment.allocations)
      .map((allocation) => allocation.stockItemKey),
  );

export const fromAllocations = (data: [string, number][]): TabularOrder => ({
  shipments: [
    {
      allocations: data.map(([stockItemKey, quantity]) => ({
        stockItemKey,
        quantity,
      })),
    },
  ],
});
